import React from 'react';
import { Icon } from '@instinet-analytics/semantic-ui-components';
import { useSelectIsAppModeStandalone } from 'app/store/general/selectors';
import { useDispatchClearActivePortfolioSelection } from 'portfolio/store/dispatcher';
import './SelectionStatus.scss';
import { PortfolioSelectionRequirements } from 'widgets/PortfolioSelectionRequirements';
import { usePortfolioSelectionRequirementsStatus } from 'widgets/hooks';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';

export interface SelectionStatusProps {
    widgetName?: string;
    selectionRequirements: PortfolioSelectionRequirements;
    suffix?: string;
}

export const portfolioMessage = (widgetName: string) =>
    `No positions selected. Select a position or portfolio of positions to use the ${widgetName}.`;
export const portfolioOfOrdersMessage = (widgetName: string) =>
    `Your input must be a portfolio of orders to use the ${widgetName}.`;

export default function SelectionStatus(props: SelectionStatusProps) {
    const { selectionRequirements, widgetName, suffix: suffixProps } = props;

    const widgetSelectionRequirementsStatus =
        usePortfolioSelectionRequirementsStatus(selectionRequirements);
    const allowClearSelections = useSelectIsAppModeStandalone();
    const clearActivePortfolioSelection =
        useDispatchClearActivePortfolioSelection();
    const { symbology } = useSelectAppSettings();

    if (widgetSelectionRequirementsStatus === 'OK') {
        return <></>;
    }

    const showRequirePortfolioWarning =
        selectionRequirements === PortfolioSelectionRequirements.Portfolio;
    const showRequirePortfolioOfOrdersWarning =
        selectionRequirements ===
        PortfolioSelectionRequirements.PortfolioOfOrders;

    const needSelection =
        selectionRequirements !== PortfolioSelectionRequirements.Portfolio &&
        selectionRequirements !==
            PortfolioSelectionRequirements.PortfolioOfOrders;
    const showMultiSelectWarning: boolean =
        needSelection && widgetSelectionRequirementsStatus === 'TOO_MANY';
    const showNoSelectionWarning: boolean =
        needSelection && widgetSelectionRequirementsStatus === 'NO_SELECTION';
    const showOrdersWithoutSymbolsWarning: boolean =
        needSelection &&
        widgetSelectionRequirementsStatus === 'ORDERS_WITHOUT_SYMBOLS';

    const portfolioOrOrdersWarning = (
        <div className="portfolio-warning">
            {portfolioOfOrdersMessage(widgetName!)}
        </div>
    );
    const portfolioWarning = (
        <div className="portfolio-warning">{portfolioMessage(widgetName!)}</div>
    );

    const suffix = suffixProps ? suffixProps : ` to use the ${widgetName}`;

    const descriptions = getDescriptions(selectionRequirements);

    const multiSelectWarning = (
        <div className="multiple-selection-warning">
            Multiple {descriptions.tooMany}s selected. View one{' '}
            {descriptions.tooMany} at a time{suffix}.
        </div>
    );

    const noSelectionWarning = (
        <div className="no-selection-warning">
            Select{' '}
            {descriptions.noSelection && !descriptions.noSelectionSingular
                ? 'at least one'
                : 'a single'}{' '}
            {descriptions.noSelection}
            {suffix}.
        </div>
    );

    const ordersWithoutSymbolsWarning = (
        <div className="orders-without-symbols-warning">
            None of your selected orders have {symbology} symbols. Select{' '}
            {descriptions.noSelection && !descriptions.noSelectionSingular
                ? 'at least one'
                : 'a single'}{' '}
            {descriptions.noSelection}
            {suffix}.
        </div>
    );

    return (
        <div className="SelectionStatus">
            <div className="start">
                <div className="info-icon">
                    <Icon name="info" />
                </div>
                {showRequirePortfolioOfOrdersWarning && (
                    <>{portfolioOrOrdersWarning}</>
                )}
                {showRequirePortfolioWarning && <>{portfolioWarning}</>}
                {showMultiSelectWarning && <>{multiSelectWarning}</>}
                {showNoSelectionWarning && <>{noSelectionWarning}</>}
                {showOrdersWithoutSymbolsWarning && (
                    <>{ordersWithoutSymbolsWarning}</>
                )}
            </div>

            {showMultiSelectWarning && allowClearSelections && (
                <div
                    className="clear-selection"
                    onClick={clearActivePortfolioSelection}
                >
                    Clear Selection
                </div>
            )}
        </div>
    );
}

export interface ControlledSelectionStatusProps {
    message: string;
}

export function ControlledSelectionStatus(
    props: ControlledSelectionStatusProps
) {
    const { message } = props;
    return (
        <div className="SelectionStatus">
            <div className="start">
                <div className="info-icon">
                    <Icon name="info" />
                </div>
                {message}
            </div>
        </div>
    );
}

function getDescriptions(requirements: PortfolioSelectionRequirements): {
    noSelection: string;
    tooMany?: string;
    noSelectionSingular?: boolean;
} {
    switch (requirements) {
        case PortfolioSelectionRequirements.ExactlyOneOrder: {
            return {
                noSelection: 'order',
                tooMany: 'order',
                noSelectionSingular: true,
            };
        }
        case PortfolioSelectionRequirements.AtLeastOneOrder: {
            return { noSelection: 'order' };
        }
        case PortfolioSelectionRequirements.PositionFromAtLeastOneOrder: {
            return { noSelection: 'order', tooMany: 'position' };
        }
        default:
            return { noSelection: 'symbol', tooMany: 'symbol' };
    }
}
